import { useRef } from 'react';

import { Flex, Skeleton, Spacer } from '@aircall/tractor';
import { ThemeProvider } from '@aircall/tractor-v2';
import { AddButton } from '@components/Button/AddButton';
import { InlineForm } from '@components/InlineForm';
import { List } from '@components/List/List';
import { ListItem } from '@components/ListItem/ListItem';
import { Paper } from '@components/Paper/Paper';
import { PhoneInputField } from '@components/PhoneInputField/PhoneInputField';
import { PhoneNumber } from '@components/PhoneNumber';
import {
  Loading,
  pipeValidators,
  validatePhoneNumberFormat,
  validateUniquePhoneNumber,
} from '@dashboard/library';
import {
  BlacklistedNumbersQuery,
  BlacklistedNumbersQuery_blacklistedNumbers_blacklistedPhoneNumbers,
  BlacklistedNumbersQuery_blacklistedNumbers_meta,
} from '@generated/BlacklistedNumbersQuery';
import {
  DeleteBlacklistedNumberMutation,
  DeleteBlacklistedNumberMutationVariables,
} from '@generated/DeleteBlacklistedNumberMutation';
import { DELETE_BLACKLISTED_NUMBER_MUTATION } from '@graphql/mutations/DeleteBlacklistedNumber';
import { BLACKLISTED_NUMBERS_QUERY } from '@graphql/queries/BlacklistedNumbersQuery';
import { formatInternational } from '@helpers/phoneNumber.helpers';
import { useDeleteEntity } from '@hooks/useDeleteEntity/useDeleteEntity';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { useQueryInfiniteList } from '@hooks/useQueryInfiniteList';
import { useToggle } from '@hooks/useToggle/useToggle';
import { useTracker } from '@hooks/useTracker/useTracker';
import { useAddBlockedNumber } from '@pages/calls-blocked-numbers/hooks/useAddBlockedNumber';
import { useTranslation } from 'react-i18next';

export function RowSkeleton() {
  return (
    <Flex alignItems='center' data-test='blocked-numbers-list-loading-more'>
      <Spacer space='xxs' alignItems='center'>
        <Skeleton width={24} height={24} shape='squircle' />
        <Skeleton width={110} height={14} />
      </Spacer>
    </Flex>
  );
}

export function getMeta(
  data: BlacklistedNumbersQuery
): BlacklistedNumbersQuery_blacklistedNumbers_meta {
  return data.blacklistedNumbers.meta;
}

export function BlockedNumbersSection() {
  const { t } = useTranslation();
  const { track } = useTracker();
  const listRef = useRef(null);
  const [shouldShowAddPhoneNumberRow, _, showAddPhoneNumberRow, onCloseClick] = useToggle();
  const { currentCompany } = useGlobalData();
  const { data, loading } = useQueryInfiniteList<BlacklistedNumbersQuery>({
    query: BLACKLISTED_NUMBERS_QUERY,
    containerRef: listRef,
    getMeta,
    queryOptions: {
      throwError: true,
    },
  });

  const { handleAddBlockedNumber } = useAddBlockedNumber();
  const [deleteBlacklistedNumber] = useDeleteEntity<
    DeleteBlacklistedNumberMutation,
    DeleteBlacklistedNumberMutationVariables
  >({
    mutation: DELETE_BLACKLISTED_NUMBER_MUTATION,
    __typename: 'BlacklistedNumber',
  });

  function handleDelete(
    number: BlacklistedNumbersQuery_blacklistedNumbers_blacklistedPhoneNumbers
  ) {
    const formattedPhoneNumber = formatInternational(number.phoneNumber);
    deleteBlacklistedNumber(number.id, { numberId: number.id }, formattedPhoneNumber);
    track({ event: 'blocked_number_deleted' });
  }

  if (loading && !data) {
    return (
      <ThemeProvider>
        <Loading data-test='blocked-number-loading' />
      </ThemeProvider>
    );
  }

  const {
    blacklistedNumbers: { blacklistedPhoneNumbers },
  } = data!;

  const numbersList = blacklistedPhoneNumbers.map((number) => number.phoneNumber);

  return (
    <Paper
      title={t('calls.pages.blocked_numbers.title')}
      subtitle={t('calls.pages.blocked_numbers.subtitle')}
    >
      <List
        ref={listRef}
        hasScroll
        isLoadingMoreItems={loading}
        customLoader={<RowSkeleton />}
        stickyFooter={
          shouldShowAddPhoneNumberRow && (
            <InlineForm
              px='xs'
              py='xxs'
              formProps={{ onSubmit: handleAddBlockedNumber }}
              onCloseClick={onCloseClick}
              submitButtonText={t('app.form.buttons.add')}
              data-test='add-blacklisted-number-inline-form'
            >
              {() => (
                <PhoneInputField
                  size='small'
                  name='phoneNumber'
                  validate={pipeValidators(
                    validateUniquePhoneNumber(numbersList),
                    validatePhoneNumberFormat
                  )}
                  defaultFlag={currentCompany.address?.country || undefined}
                />
              )}
            </InlineForm>
          )
        }
      >
        {blacklistedPhoneNumbers.map((number, index) => (
          <ListItem
            key={number.id}
            dropdownOptions={[
              {
                title: t('calls.pages.blocked_numbers.delete'),
                callback: () => handleDelete(number),
              },
            ]}
            data-test={`blocked-number-list-item-${index}`}
          >
            <PhoneNumber
              number={number.phoneNumber}
              highlightInvalidFormattablePhoneNumber
              pl='s'
            />
          </ListItem>
        ))}
      </List>

      {!shouldShowAddPhoneNumberRow && (
        <AddButton
          onClick={showAddPhoneNumberRow}
          hasData={!!blacklistedPhoneNumbers.length}
          translationKey='calls.pages.blocked_numbers.add'
          data-test='blocked-number-add'
        />
      )}
    </Paper>
  );
}
